<template>
  <header ref="header">
    <div class="container">
      <NuxtLink
        to="/"
        class="logo"
      >
        <img
          :src="logoSrc"
          alt="Retool Your School"
        >
      </NuxtLink>

      <div>
        <nav class="menu">
          <ul class="menu-top">
            <li
              v-for="menuItem of headerTopNav"
              :key="menuItem.id"
            >
              <NuxtLink
                :to="menuItem.url"
                class="btn small"
              >
                {{ menuItem.title }}
              </NuxtLink>
            </li>
          </ul>

          <ul class="menu-main">
            <li
              v-for="menuItem of headerNav"
              :key="menuItem.id"
            >
              <NuxtLink
                :to="menuItem.url"
                class=""
              >
                {{ menuItem.title }}
                <font-awesome-icon
                  v-if="menuItem.has_children"
                  icon="chevron-down"
                />
              </NuxtLink>

              <ul
                v-if="menuItem.has_children"
                class="submenu"
              >
                <li
                  v-for="submenuItem of menuItem.children"
                  :key="submenuItem.id"
                >
                  <NuxtLink
                    :to="submenuItem.url"
                    class=""
                  >
                    {{ submenuItem.title }}
                  </NuxtLink>
                </li>
              </ul>
            </li>
          </ul>
        </nav>

        <button
          class="menu-btn"
          :aria-label="menuIsVisible ? 'Close Menu' : 'Open Menu'"
          @click="onMenuBtnClick"
          @keydown.enter="onMenuBtnClick"
        >
          <font-awesome-icon :icon="menuIsVisible ? 'xmark' : 'bars'" />
        </button>
      </div>
    </div>

    <transition
      enter-active-class="animate__animated animate__fadeInRight animate__faster"
      leave-active-class="animate__animated animate__fadeOutRight animate__faster"
    >
      <div
        v-if="menuIsVisible"
        class="mobile-menu"
        :style="{ height: `calc(100vh - ${headerHeight})` }"
      >
        <nav>
          <ul class="main">
            <li
              v-for="menuItem of mobileNav"
              :key="menuItem.id"
            >
              <NuxtLink
                :to="menuItem.url"
                class=""
              >
                {{ menuItem.title }}
              </NuxtLink>

              <ul class="submenu">
                <li
                  v-for="submenuItem of menuItem.children"
                  :key="submenuItem.id"
                >
                  <NuxtLink
                    :to="submenuItem.url"
                    class=""
                  >
                    {{ submenuItem.title }}
                  </NuxtLink>
                </li>
              </ul>
            </li>
          </ul>

          <ul class="social">
            <li
              v-for="menuItem of socialLinks"
              :key="menuItem.id"
            >
              <a
                :href="menuItem.url"
                target="_blank"
                class=""
                :aria-label="menuItem.title"
              >
                <font-awesome-icon :icon="['fab', menuItem.acf.icon.id]" />
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script>
import { mapState } from 'pinia';

import logoSrc from '@/assets/imgs/logo.png';

export default {
  data() {
    return {
      logoSrc,
      menuIsVisible: false,
      headerHeight: '0'
    };
  },

  computed: {
    ...mapState(useMenuStore, [
      'headerNav',
      'headerTopNav',
      'socialLinks'
    ]),

    mobileNav() {
      return [
        ...(this.headerTopNav || []),
        ...(this.headerNav || [])
      ];
    }
  },

  watch: {
    menuIsVisible(menuIsVisible) {
      if (menuIsVisible) {
        document.body.style.height = '100%';
        document.body.style.overflow = 'hidden';
      }
      else {
        document.body.style.height = '';
        document.body.style.overflow = '';
      }
    },

    $route: {
      deep: true,
      handler() {
        this.menuIsVisible = false;
        document.activeElement.blur();
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    this.menuIsVisible = false;
  },

  methods: {
    onWindowResize() {
      const { header } = this.$refs;

      this.headerHeight = getComputedStyle(header).height;

      if (this.menuIsVisible && window.innerWidth > 1170) {
        this.menuIsVisible = false;
      }
    },

    onMenuBtnClick() {
      this.menuIsVisible = !this.menuIsVisible;
    }
  }
};
</script>

<style lang="scss" scoped>
header {
  background: $thd-black;

  > .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .logo {
    margin: 16px 0;
    height: 60px;
    display: flex;
    align-items: center;

    > img {
      height: 52px;
    }
  }

  .menu {
    display: none;

    @media screen and (min-width: $breakpoint-lg) {
      display: block;
    }

    ul {
      padding: 0;
      margin: 0;
      display: flex;
      margin-right: -28px;

      li {
        position: relative;
        list-style: none;
      }

      &.menu-main {
        li {
          a {
            display: block;
            padding: 16px 28px;
            color: $thd-white;
            font-size: 18px;
            font-weight: 700;
            line-height: 24px;
            font-stretch: condensed;
            text-decoration: none;
            white-space: nowrap;

            &:visited {
              color: $thd-white;
            }
          }

          .submenu {
            display: none;
            position: absolute;
            top: 100%;
            left: 0%;
            background: $thd-black;
            border-top: 2px solid $thd-orange;
          }

          &:hover,
          &:focus-within {
            > a {
              color: $thd-orange;
            }

            .submenu {
              display: block;
            }
          }
        }
      }

      &.menu-top {
        display: flex;
        justify-content: flex-end;
        margin-right: 0;
        margin-top: 16px;

        li {
          /* &:first-child {
            @media screen and (min-width: $breakpoint-lg) {
              display: none;
            }
          } */

          a {
            font-size: 16px;
            line-height: 14px;

            &:hover {
              border-color: $thd-white;
            }
          }
        }

        & > li:not(:first-child) {
          margin-left: 16px;
        }
      }
    }
  }

  .menu-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: $thd-white;
    cursor: pointer;

    @media screen and (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  .mobile-menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    color: $thd-white;
    background: $thd-black;
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
    font-stretch: condensed;
    text-transform: uppercase;
    text-decoration: none;
    overflow: hidden scroll;

    a {
      color: inherit;
      text-decoration: none;

      &:visited {
        color: inherit;
      }
    }

    .main {
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      margin-right: -28px;

      li {
        position: relative;
        list-style: none;

        a {
          display: block;
          padding: 12px 28px;
        }

        .submenu a {
          font-size: 20px;
        }

        &:hover,
        &:focus-within {
          > a {
            color: $thd-orange;
          }

          .submenu {
            display: block;
          }
        }
      }

      /* & > li:nth-child(-n+3) a {
        color: $thd-orange;

        &:hover {
          color: $thd-white;
        }
      } */
    }

    .social {
      padding: 0;
      margin: 32px 0;
      display: flex;
      justify-content: space-evenly;

      li {
        list-style: none;

        a {
          display: flex;
          justify-content: center;
          align-items: center;
          color: $thd-black;
          background: $thd-white;
          height: 40px;
          width: 40px;
          border-radius: 9999px;
        }
      }
    }
  }
}
</style>
